import { useMemo, useState } from 'react'
import useSWR from 'swr'
import { useDebounce } from '../hooks'
import { useToast } from '../contexts/ToastContext'
import {
  fetchPromotions,
  fetchConflicts,
  deletePromotion,
  type Promotion,
  type PromotionStatus,
} from '../services/promotionsApi'
import { Badge, Button } from '../components'
import type { BadgeVariant } from '../components'
import PromotionForm from './PromotionForm'
import pageStyles from '../styles/page.module.scss'
import listTableStyles from '../styles/list-table.module.scss'
import styles from './PromotionsList.module.scss'

const STATUS_VARIANT: Record<PromotionStatus, BadgeVariant> = {
  DRAFT: 'neutral',
  SCHEDULED: 'secondary',
  LIVE: 'live',
  EXPIRED: 'warning',
}

const formatWindow = (promo: Promotion): string => {
  const fmt = (iso: string | null) =>
    iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : null
  const start = fmt(promo.starts_at)
  const end = fmt(promo.ends_at)
  if (!start && !end) return 'Always'
  return `${start ?? '—'} → ${end ?? '—'}`
}

export default function PromotionsList() {
  const { data, isLoading, error, mutate } = useSWR('promotions', fetchPromotions, {
    revalidateOnFocus: false,
  })
  // Offers that overlapping enabled promotions compete for. Keyed by promotion id
  // so each row can flag whether it collides with another — and whether its
  // discount is the one ACE actually serves.
  const { data: conflictsData } = useSWR('promotion-conflicts', () => fetchConflicts(), {
    revalidateOnFocus: false,
  })
  const { toast } = useToast()

  const [searchTerm, setSearchTerm] = useState('')
  const debouncedSearch = useDebounce(searchTerm, 300)

  // null = closed; '' = create (new); id = edit that promotion.
  const [editing, setEditing] = useState<string | null | undefined>(undefined)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const promotions = useMemo(() => data?.promotions ?? [], [data])

  const conflictByPromo = useMemo(() => {
    const map = new Map<string, { loses: boolean; offers: number }>()
    for (const conflict of conflictsData?.conflicts ?? []) {
      for (const p of conflict.promotions) {
        if (!p.tc_promotion_id) continue
        const entry = map.get(p.tc_promotion_id) ?? { loses: false, offers: 0 }
        entry.offers += 1
        if (!p.wins) entry.loses = true
        map.set(p.tc_promotion_id, entry)
      }
    }
    return map
  }, [conflictsData])

  const filtered = useMemo(() => {
    const query = debouncedSearch.trim().toLowerCase()
    if (!query) return promotions
    return promotions.filter((p) =>
      `${p.tc_name ?? ''} ${p.code} ${p.coupon_code}`.toLowerCase().includes(query)
    )
  }, [promotions, debouncedSearch])

  const handleDelete = async (promo: Promotion) => {
    if (!window.confirm(`Delete promotion "${promo.tc_name || promo.code}"? This cannot be undone.`)) return
    setDeletingId(promo.tc_promotion_id)
    try {
      await deletePromotion(promo.tc_promotion_id)
      await mutate()
      toast('Promotion deleted', 'success')
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to delete promotion', 'error')
    } finally {
      setDeletingId(null)
    }
  }

  if (error) {
    return (
      <div className={pageStyles.pageContainer}>
        <div className={pageStyles.error}>
          <h2>Couldn’t load promotions</h2>
          <p>{(error as Error).message}</p>
          <Button variant="secondary" onClick={() => void mutate()}>Retry</Button>
        </div>
      </div>
    )
  }

  return (
    <div className={pageStyles.pageContainer}>
      <div className={pageStyles.headerButtons}>
        <Button onClick={() => setEditing('')}>New promotion</Button>
      </div>

      <div className={pageStyles.pageHeader}>
        <h1>Promotions</h1>
        <p>
          Turn a Cleeng coupon into a merchandised promotion: assign it to eligible offers, set a
          display window, and enable it. Coupons themselves are created in the Cleeng console.
        </p>
      </div>

      <div className={listTableStyles.searchSection}>
        <div className={listTableStyles.searchForm}>
          <div className={listTableStyles.searchInputGroup}>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, code, or coupon…"
              className={listTableStyles.searchInput}
            />
            {searchTerm && (
              <button type="button" onClick={() => setSearchTerm('')} className={listTableStyles.clearButton}>
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {isLoading && !data ? (
        <div className={pageStyles.loading}>
          <h3>Loading…</h3>
        </div>
      ) : filtered.length === 0 ? (
        <div className={listTableStyles.noResults}>
          <h3>No promotions {debouncedSearch ? 'match your search' : 'yet'}</h3>
          {!debouncedSearch && <p>Create one from a Cleeng coupon to get started.</p>}
        </div>
      ) : (
        <>
          <div className={`${listTableStyles.tableHeader} ${styles.listHeader}`}>
            <div>Promotion</div>
            <div>Coupon</div>
            <div>Window</div>
            <div>Offers</div>
            <div>Status</div>
            <div></div>
          </div>
          <div className={listTableStyles.tableBody}>
            {filtered.map((promo) => {
              const conflict = conflictByPromo.get(promo.tc_promotion_id)
              return (
              <div key={promo.tc_promotion_id} className={`${listTableStyles.tableRow} ${styles.listItem}`}>
                <div className={listTableStyles.cellPrimary}>
                  <p className={listTableStyles.primaryText}>{promo.tc_name || promo.code}</p>
                  <p className={listTableStyles.secondaryText}>{promo.code}</p>
                </div>
                <div className={listTableStyles.cellId}>{promo.coupon_code}</div>
                <div className={listTableStyles.cellText}>{formatWindow(promo)}</div>
                <div className={listTableStyles.cellText}>{promo.offer_codes.length}</div>
                <div className={listTableStyles.cellBadges}>
                  <Badge variant={STATUS_VARIANT[promo.status]}>{promo.status}</Badge>
                  {conflict && (
                    <span
                      title={
                        conflict.loses
                          ? `Overridden on ${conflict.offers} offer${conflict.offers === 1 ? '' : 's'} by a higher-discount promotion`
                          : `Overlaps another promotion on ${conflict.offers} offer${conflict.offers === 1 ? '' : 's'}; this one wins`
                      }
                    >
                      <Badge variant={conflict.loses ? 'warning' : 'secondary'}>
                        {conflict.loses ? 'Overridden' : 'Overlaps'}
                      </Badge>
                    </span>
                  )}
                </div>
                <div className={`${listTableStyles.cellActions} ${styles.actionsCell}`}>
                  <Button variant="secondary" size="small" onClick={() => setEditing(promo.tc_promotion_id)}>
                    Edit
                  </Button>
                  <Button
                    variant="danger"
                    size="small"
                    disabled={deletingId === promo.tc_promotion_id}
                    onClick={() => void handleDelete(promo)}
                  >
                    Delete
                  </Button>
                </div>
              </div>
              )
            })}
          </div>
        </>
      )}

      {editing !== undefined && (
        <PromotionForm
          isOpen
          promotionId={editing || null}
          onClose={() => setEditing(undefined)}
          onSaved={() => {
            setEditing(undefined)
            void mutate()
          }}
        />
      )}
    </div>
  )
}
